import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    EmailAuthProvider,
    GoogleAuthProvider,
    reauthenticateWithCredential,
    reauthenticateWithPopup,
    updatePassword
} from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { environment } from '../../../config/environment';
import { useAuth } from '../../../context/AuthContext';
import { useNotificationService } from '../../../services/notificationService';
import { useTransactionalEmailService } from '../../../services/transactionalEmailService';
import { useUserService } from '../../../services/user.service';
import type { NotificationSettings as NotificationSettingsType } from '../../../types/user';

type MutationCallbacks = {
    onSuccess?: () => void;
    onError?: (error: any) => void;
};

type UpdateNotificationParams = MutationCallbacks & {
    setting: keyof NotificationSettingsType;
};

type UpdateEmailParams = MutationCallbacks & {
    newEmail: string;
    currentPassword: string;
};

type UpdatePasswordParams = MutationCallbacks & {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
};

type DeleteAccountParams = MutationCallbacks & {
    isGoogleUser: boolean;
    deletePassword?: string;
};

export const useSettingsQueries = (
    defaultNotificationSettings: NotificationSettingsType,
    onNotificationError: (error: any) => void,
    isGoogleUser: boolean
) => {
    const { currentUser, loginWithGoogle } = useAuth();
    const userService = useUserService();
    const notificationService = useNotificationService();
    const transactionalEmailService = useTransactionalEmailService();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    // Query for notification settings
    const {
        data: notificationSettings = defaultNotificationSettings,
        error: notificationError,
        isLoading: isNotificationsLoading,
    } = useQuery({
        queryKey: ['notifications', currentUser?.uid],
        queryFn: () => {
            try {
                return notificationService.getUserNotifications();
            } catch (err: any) {
                onNotificationError(err);
                throw err;
            }
        },
        enabled: !!currentUser,
    });

    // Mutation for updating notification settings
    const updateNotificationMutation = useMutation({
        mutationKey: ['updateNotification'],
        mutationFn: async ({ setting }: UpdateNotificationParams) => {
            if (!currentUser) throw new Error('No user logged in');
            if (!notificationSettings) throw new Error('No notification settings found');

            const updatedSettings = {
                ...notificationSettings,
                [setting]: !notificationSettings[setting],
            };

            return notificationService.updateNotifications(updatedSettings);
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
            variables.onSuccess?.();
        },
        onError: (error, variables) => {
            variables.onError?.(error);
        },
    });

    // Mutation for deleting account
    const deleteAccountMutation = useMutation({
        mutationKey: ['deleteAccount'],
        mutationFn: async ({ isGoogleUser, deletePassword }: DeleteAccountParams) => {
            if (!currentUser) throw new Error('No user logged in');

            // Re-authenticate based on auth type
            if (!isGoogleUser) {
                if (!deletePassword) throw new Error('Password is required');
                const credential = EmailAuthProvider.credential(currentUser.email!, deletePassword);
                await reauthenticateWithCredential(currentUser, credential);
                
            } else {
                if (environment.production) {
                    await loginWithGoogle('/settings?deleteAccount=true');
                    return;
                } else {                    
                    await reauthenticateWithPopup(currentUser, new GoogleAuthProvider());
                }
            }
            await userService.deleteAccount();
            await currentUser.delete();
            navigate('/login');
        },
        onSuccess: (_, variables) => {
            variables.onSuccess?.();
        },
        onError: (error, variables) => {
            variables.onError?.(error);
        },
    });

    // Mutation for updating email
    const updateEmailMutation = useMutation({
        mutationKey: ['updateEmail'],
        mutationFn: async ({ newEmail, currentPassword }: UpdateEmailParams) => {
            if (!currentUser) throw new Error('No user logged in');
            if (!currentUser.email) throw new Error('Current user email is missing');

            // Re-authenticate the user if not using Google auth
            if (!isGoogleUser) {
                const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
                await reauthenticateWithCredential(currentUser, credential);
            }

            const response = await transactionalEmailService.sendEmailChange(currentUser.email, newEmail);

            if (!response.success) {
                throw new Error(response.message || 'Failed to send email change verification');
            }

            return response;
        },
        onSuccess: (_, variables) => {
            variables.onSuccess?.();
        },
        onError: (error, variables) => {
            variables.onError?.(error);
        },
    });

    // Mutation for updating password
    const updatePasswordMutation = useMutation({
        mutationKey: ['updatePassword'],
        mutationFn: async ({ currentPassword, newPassword, confirmPassword }: UpdatePasswordParams) => {
            if (!currentUser) throw new Error('No user logged in');
            if (newPassword !== confirmPassword) {
                throw new Error('Passwords do not match');
            }

            const credential = EmailAuthProvider.credential(currentUser.email!, currentPassword);
            await reauthenticateWithCredential(currentUser, credential);

            await updatePassword(currentUser, newPassword);
        },
        onSuccess: (_, variables) => {
            variables.onSuccess?.();
        },
        onError: (error, variables) => {
            variables.onError?.(error);
        },
    });

    return {
        // Queries
        notificationSettings,
        isNotificationsLoading,

        // Mutations
        updateNotification: updateNotificationMutation.mutate,
        isUpdatingNotification: updateNotificationMutation.isPending,
        deleteAccount: deleteAccountMutation.mutate,
        isDeleting: deleteAccountMutation.isPending,
        updateEmail: updateEmailMutation.mutate,
        isEmailUpdating: updateEmailMutation.isPending,
        updatePassword: updatePasswordMutation.mutate,
        isPasswordUpdating: updatePasswordMutation.isPending,
    };
};
